import { useState } from "react";
import { FaCalendarTimes, FaChevronLeft, FaChevronRight, FaCreditCard, FaSyncAlt } from "react-icons/fa";
import PageLoading from "src/components/pageLoading";
import { useTheme } from "src/context/theme.context";
import { BillingExceptionsTable } from "../components/billing-exceptions-table";
import { useGetBillingExceptionsQuery } from "../billing-exceptions.service";
import { BillingExceptionScope } from "../types/billing-exception.entity";
import {
  StoreSubscriptionVerificationModal,
  useStoreSubscriptionVerification,
} from "src/features/subscription-users/components/store-subscription-verification";
import { ReconcileStripePaymentFollowUps } from "../components/reconcile-stripe-payment-follow-ups";

const tabs: { id: BillingExceptionScope; label: string; icon: JSX.Element }[] = [
  { id: "payments", label: "Payment follow-ups", icon: <FaCreditCard /> },
  { id: "subscriptions", label: "Subscription exceptions", icon: <FaCalendarTimes /> },
];

const emptyStateCopy: Record<BillingExceptionScope, string> = {
  payments: "No uncompleted checkouts are older than one hour.",
  subscriptions: "No active subscriptions are past their end date outside grace period.",
};

const BillingExceptionsPage = () => {
  const [pagination, setPagination] = useState<{
    scope: BillingExceptionScope;
    pageIndex: number;
    offsets: (string | undefined)[];
  }>({
    scope: "payments",
    pageIndex: 0,
    offsets: [undefined],
  });
  const { isDarkMode } = useTheme();
  const storeSubscriptionVerification = useStoreSubscriptionVerification();
  const offset = pagination.offsets[pagination.pageIndex];
  const { data, isLoading, isFetching, isError, refetch } = useGetBillingExceptionsQuery({
    scope: pagination.scope,
    offset,
  });
  const isBusy = isLoading || isFetching;

  const selectScope = (scope: BillingExceptionScope) => {
    if (scope === pagination.scope) return;

    setPagination({ scope, pageIndex: 0, offsets: [undefined] });
  };

  const previousPage = () => {
    setPagination((current) => ({ ...current, pageIndex: current.pageIndex - 1 }));
  };

  const nextPage = () => {
    if (!data?.nextOffset) return;

    setPagination((current) => ({
      ...current,
      pageIndex: current.pageIndex + 1,
      offsets: [...current.offsets.slice(0, current.pageIndex + 1), data.nextOffset],
    }));
  };

  return (
    <div className="page_container">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className={`mb-1 text-sm font-medium ${isDarkMode ? "text-sky-300" : "text-sky-700"}`}>Billing operations</p>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? "text-slate-50" : "text-slate-900"}`}>Billing Exceptions</h1>
        </div>
        <div className="flex items-center gap-2">
          {pagination.scope === "payments" && (
            <ReconcileStripePaymentFollowUps
              isDarkMode={isDarkMode}
              onComplete={refetch}
            />
          )}
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isBusy}
            title="Refresh exceptions"
            className={`inline-flex h-10 w-10 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              isDarkMode
                ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                : "border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50"
            }`}
          >
            <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className={`mb-4 flex w-full border-b ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
        {tabs.map((tab) => {
          const isActive = pagination.scope === tab.id;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectScope(tab.id)}
              className={`inline-flex min-h-[44px] items-center gap-2 border-b-2 px-4 text-sm font-medium transition-colors ${
                isActive
                  ? isDarkMode
                    ? "border-sky-400 text-sky-300"
                    : "border-sky-600 text-sky-700"
                  : isDarkMode
                    ? "border-transparent text-slate-400 hover:text-slate-200"
                    : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <PageLoading />
      ) : isError ? (
        <div className={`border p-4 text-sm ${isDarkMode ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          Could not load billing exceptions. Please try again.
        </div>
      ) : data?.items.length ? (
        <>
          <div className={`mb-3 flex items-center justify-between text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            <span>{data.items.length} current exception{data.items.length === 1 ? "" : "s"}</span>
            <span>Page {pagination.pageIndex + 1}</span>
          </div>
          <BillingExceptionsTable
            data={data.items}
            isDarkMode={isDarkMode}
            isVerifyingStoreSubscription={storeSubscriptionVerification.isLoading}
            onVerifyStoreSubscription={storeSubscriptionVerification.verify}
          />
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={previousPage}
              disabled={pagination.pageIndex === 0 || isBusy}
              title="Previous page"
              className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                isDarkMode
                  ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                  : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"
              }`}
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              onClick={nextPage}
              disabled={!data.nextOffset || isBusy}
              title="Next page"
              className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                isDarkMode
                  ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                  : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"
              }`}
            >
              <FaChevronRight />
            </button>
          </div>
        </>
      ) : (
        <div className={`border px-6 py-12 text-center ${isDarkMode ? "border-slate-700 bg-slate-900/50 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
          <p>{emptyStateCopy[pagination.scope]}</p>
          {pagination.pageIndex > 0 && (
            <button
              type="button"
              onClick={previousPage}
              title="Previous page"
              className={`mt-4 inline-flex h-9 w-9 items-center justify-center rounded border transition-colors ${
                isDarkMode
                  ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15"
                  : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50"
              }`}
            >
              <FaChevronLeft />
            </button>
          )}
        </div>
      )}
      <StoreSubscriptionVerificationModal
        {...storeSubscriptionVerification.modalProps}
      />
    </div>
  );
};

export default BillingExceptionsPage;
